import {
  AnimationConfig,
  CameraManager,
  Character,
  CharacterDescription,
  CharacterManager,
  CharacterModelLoader,
  CharacterState,
  CollisionsManager,
  Composer,
  EulXYZ,
  KeyInputManager,
  MMLCompositionScene,
  TimeManager,
  Vect3,
} from "@mml-io/3d-web-client-core";
import { IMMLScene } from "@mml-io/mml-web";
import {
  EditableNetworkedDOM,
  NetworkedDOM,
} from "@mml-io/networked-dom-document";
import { AudioListener, Euler, Scene, Vector3 } from "three";

import { MMLCompositionClient } from "~/library/mml/compositionClient";

import hdriPath from "./assets/hdr/industrial_sunset_2k.hdr";
import airAnimationFileUrl from "./assets/models/anim_air.glb";
import doubleJumpAnimationFileUrl from "./assets/models/anim_double_jump.glb";
import idleAnimationFileUrl from "./assets/models/anim_idle.glb";
import jogAnimationFileUrl from "./assets/models/anim_jog.glb";
import sprintAnimationFileUrl from "./assets/models/anim_run.glb";
import meshFileUrl from "./assets/models/bot.glb";
import { LocalAvatarServer } from "./LocalAvatarServer";
import { Room } from "./Room";

const animationConfig: AnimationConfig = {
  idleAnimationFileUrl,
  jogAnimationFileUrl,
  sprintAnimationFileUrl,
  airAnimationFileUrl,
  doubleJumpAnimationFileUrl,
};

const characterDescription: CharacterDescription = {
  meshFileUrl,
};

export class LocalAvatarClient {
  public element: HTMLDivElement;

  private readonly scene = new Scene();
  private readonly audioListener = new AudioListener();
  public readonly composer: Composer;
  private readonly timeManager = new TimeManager();
  private readonly keyInputManager = new KeyInputManager(() => {
    return this.cameraManager.hasActiveInput();
  });
  private readonly characterManager: CharacterManager;
  private readonly cameraManager: CameraManager;

  private readonly collisionsManager = new CollisionsManager(this.scene);
  private readonly remoteUserStates = new Map<number, CharacterState>();

  private mmlComposition: MMLCompositionScene;
  private resizeObserver: ResizeObserver;
  private documentRunnerClients = new Set<MMLCompositionClient>();
  private animationFrame: number;
  private characterModelLoader = new CharacterModelLoader();
  private room: Room;
  private roomCollisionsEnabled = true;

  constructor(
    private localAvatarServer: LocalAvatarServer,
    private localClientId: number,
    spawnPosition: Vector3,
    spawnRotation: Euler,
  ) {
    this.element = document.createElement("div");
    this.element.style.position = "absolute";
    this.element.style.width = "100%";
    this.element.style.height = "100%";

    document.addEventListener("mousedown", () => {
      if (this.audioListener.context.state === "suspended") {
        this.audioListener.context.resume();
      }
    });

    this.cameraManager = new CameraManager(
      this.element,
      this.collisionsManager,
      Math.PI / 2,
      Math.PI / 2,
    );
    this.cameraManager.camera.add(this.audioListener);

    this.composer = new Composer({
      scene: this.scene,
      cameraManager: this.cameraManager,
      spawnSun: true,
    });
    this.composer.useHDRI(hdriPath);
    this.element.appendChild(this.composer.renderer.domElement);

    this.resizeObserver = new ResizeObserver(() => {
      this.composer.fitContainer();
    });
    this.resizeObserver.observe(this.element);

    this.localAvatarServer.addClient(
      localClientId,
      (clientId: number, userNetworkingClientUpdate: null | CharacterState) => {
        if (userNetworkingClientUpdate === null) {
          this.remoteUserStates.delete(clientId);
        } else {
          this.remoteUserStates.set(clientId, userNetworkingClientUpdate);
        }
      },
    );

    const animationsPromise = Character.loadAnimations(
      this.characterModelLoader,
      animationConfig,
    );

    this.characterManager = new CharacterManager({
      animationsPromise,
      sendLocalCharacterColors(): void {
        // no-op
      },
      composer: this.composer,
      characterModelLoader: this.characterModelLoader,
      collisionsManager: this.collisionsManager,
      cameraManager: this.cameraManager,
      timeManager: this.timeManager,
      keyInputManager: this.keyInputManager,
      remoteUserStates: this.remoteUserStates,
      sendUpdate: (characterState: CharacterState) => {
        localAvatarServer.send(localClientId, characterState);
      },
      spawnConfiguration: {
        spawnPosition,
        spawnYRotation: spawnRotation.y,
        spawnPositionVariance: new Vector3(0, 0, 0),
        enableRespawnButton: false,
        respawnTrigger: {
          minX: Number.NEGATIVE_INFINITY,
          maxX: Number.POSITIVE_INFINITY,
          minY: -100,
          maxY: Number.POSITIVE_INFINITY,
          minZ: Number.NEGATIVE_INFINITY,
          maxZ: Number.POSITIVE_INFINITY,
        },
      },
      characterResolve: (clientId: number) => {
        return {
          username: "User " + clientId,
          characterDescription,
          colors: [],
        };
      },
    });
    this.scene.add(this.characterManager.group);

    this.mmlComposition = new MMLCompositionScene({
      targetElement: this.element,
      renderer: this.composer.renderer,
      scene: this.scene,
      camera: this.cameraManager.camera,
      audioListener: this.audioListener,
      collisionsManager: this.collisionsManager,
      getUserPositionAndRotation: () => {
        return this.characterManager.getLocalCharacterPositionAndRotation();
      },
    });
    this.scene.add(this.mmlComposition.group);

    this.room = new Room();
    this.collisionsManager.addMeshesGroup(this.room);
    this.scene.add(this.room);

    this.characterManager.spawnLocalCharacter(
      localClientId,
      "Local User",
      characterDescription!,
      new Vect3(spawnPosition.x, spawnPosition.y, spawnPosition.z),
      new EulXYZ(spawnRotation.x, spawnRotation.y, spawnRotation.z),
    );
  }

  public dispose() {
    cancelAnimationFrame(this.animationFrame);
    for (const documentRunnerClient of this.documentRunnerClients) {
      documentRunnerClient.dispose();
    }
    this.localAvatarServer.removeClient(this.localClientId);
    this.documentRunnerClients.clear();
    this.resizeObserver.disconnect();
    this.mmlComposition.dispose();
    this.characterManager.clear();
    this.cameraManager.dispose();
    this.composer.dispose();
    this.element.remove();
  }

  public update(): void {
    this.timeManager.update();
    this.characterManager.update();
    this.cameraManager.update();
    this.composer.sun?.updateCharacterPosition(
      this.characterManager.localCharacter?.position,
    );
    this.composer.render(this.timeManager);
    this.animationFrame = requestAnimationFrame(() => {
      this.update();
    });
  }

  public addDocument(
    mmlDocument: NetworkedDOM | EditableNetworkedDOM | string,
    windowTarget: Window,
    remoteHolderElement: HTMLElement,
  ) {
    const mmlWebRunnerClient = new MMLCompositionClient(
      windowTarget,
      remoteHolderElement,
      // @mml-io/3d-web-client-core is using the IMMLScene interface from mml-web 0.16.0
      this.mmlComposition.mmlScene as unknown as IMMLScene,
    );
    if (typeof mmlDocument === "string") {
      mmlWebRunnerClient.connectToSocket(mmlDocument);
    } else {
      mmlWebRunnerClient.connectToDocument(mmlDocument);
    }
    this.documentRunnerClients.add(mmlWebRunnerClient);
  }

  setRoomVisible(visible: boolean) {
    this.room.setVisible(visible);

    if (visible && !this.roomCollisionsEnabled) {
      this.collisionsManager.addMeshesGroup(this.room);
      this.roomCollisionsEnabled = true;
    } else if (!visible && this.roomCollisionsEnabled) {
      this.collisionsManager.removeMeshesGroup(this.room);
      this.roomCollisionsEnabled = false;
    }
  }
}
